"use client";

import { useEffect, useState, useTransition } from "react";
import { useParams, useRouter } from "next/navigation";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import SearchBar from "../../../components/SearchBar/SearchBar";
import BlogsCard from "./components/BlogsCard";
import { Newsletter } from "../../../components/Ads/Ads";
import Pagination from "../../../components/Pagination/Pagination";
import { useArticles } from "../../../Hooks/useArticles";
import "./Blogs.css";
import "../../einbuergerungstest/mocktest/bavaria/MockTest.css";
import "../../einbuergerungstest/ToolDetailsPage.css";
import { Placeholder } from "react-bootstrap";
import { BlogsBottomAds, BlogsSideAds } from "@/components/Ads/Blogspage_Ads";
import Loading from "./[slug]/loading";

const toValidImageUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\/[^ "]+$/i.test(trimmed) || trimmed.startsWith("/")) return trimmed;
  return "";
};

const getPostImage = (post) => {
  return (
    toValidImageUrl(post?.jetpack_featured_media_url) ||
    toValidImageUrl(post?._embedded?.["wp:featuredmedia"]?.[0]?.source_url) ||
    toValidImageUrl(post?.yoast_head_json?.og_image?.[0]?.url) ||
    toValidImageUrl(post?.featured_img_url) ||
    ""
  );
};

export default function BlogsPage({ fromSearch = false, searchData = {} }) {
  const router = useRouter();
  const [isNavigating, startNavigationTransition] = useTransition();
  const params = useParams();
  const category = params?.category;

  const [currentPage, setCurrentPage] = useState(1);
  const [articles, setArticles] = useState([]);
  const [categoryName, setCategoryName] = useState(
    searchData.categoryName || ""
  );
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(searchData.totalPages || 1);

  const {
    posts: fetchedPosts,
    loading: fetchedLoading,
    categoryId,
    totalPages: fetchedTotalPages,
  } = useArticles(category, currentPage);

  const {
    posts: searchPosts = [],
    totalPages: searchTotal = 1,
    categoryName: searchCategory = "",
    loading: searchLoading = false,
  } = searchData;

  useEffect(() => {
    if (fromSearch) {
      if (searchPosts && searchPosts.length > 0) {
        const formatted = searchPosts.map((post) => ({
          title: post.title?.rendered || "Untitled",
          content:
            post.excerpt?.rendered?.replace(/<[^>]*>?/gm, "").slice(0, 160) ||
            "",
          date: post.date
            ? new Date(post.date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
            : "Unknown date",
          category: post.categories_details?.[0]?.name || "Other",
          image: getPostImage(post),
          slug: post.slug,
          tag: post.tags_details?.[0]?.name || "Other",
        }));

        setArticles(formatted);
      } else {
        setArticles([]);
      }

      setCategoryName(searchCategory || "Search Results");
      setTotalPages(searchTotal);
      setLoading(searchLoading);
    }
  }, [fromSearch, searchPosts, searchCategory, searchTotal, searchLoading]);

  useEffect(() => {
    if (!fromSearch) {
      if (fetchedLoading) {
        setLoading(true);
        return;
      }

      if (!fetchedPosts || fetchedPosts.length === 0) {
        setArticles([]);
        setCategoryName("");
        setLoading(false);
        setTotalPages(1);
        return;
      }

      const formatted = fetchedPosts.map((post) => ({
        title: post.title?.rendered || "Untitled",
        content:
          post.excerpt?.rendered?.replace(/<[^>]*>?/gm, "").slice(0, 160) || "",
        date: post.date
          ? new Date(post.date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
          : "Unknown date",
        category: post.categories_details?.[0]?.name || "Other",
        image: getPostImage(post),
        slug: post.slug,
        tag: post.tags_details?.[0]?.name || "Other",
      }));

      setArticles(formatted);
      setTotalPages(fetchedTotalPages);
      setLoading(false);

      const matchedCategory = fetchedPosts[0]?.categories_details?.find(
        (c) => c.id === categoryId
      );
      setCategoryName(
        matchedCategory?.name || formatted[0]?.category || "Uncategorized"
      );
    }
  }, [fromSearch, fetchedPosts, fetchedLoading, fetchedTotalPages, categoryId]);

  const handleArticleClick = (articleSlug) => {
    if (!articleSlug) return;
    startNavigationTransition(() => {
      router.push(`/blogs/${category}/${articleSlug}`);
    });
  };

  if (isNavigating) {
    return (
      <>
        <Loading />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="page-container">
        {!fromSearch &&
          (loading ? (
            <div className="mb-4">
              <Placeholder as="h1" animation="glow">
                <Placeholder xs={5} />
              </Placeholder>
            </div>
          ) : (
            <h1 className="page-title">{categoryName}</h1>
          ))}

        <div className="content-container_wrapper">
          <div className="content-container">
            {fromSearch ? (
              <>
                <SearchBar />
                <div className="Search-results">
                  {loading ? (
                    <div className="mb-4">
                      <Placeholder as="p" animation="glow">
                        <Placeholder xs={4} /> <br /> <Placeholder xs={2} />
                      </Placeholder>
                    </div>
                  ) : (
                    <>
                      Showing results for
                      <span className="text-style-2">
                        {" "}
                        “{categoryName || searchCategory || "..."}”
                      </span>
                      <br />
                      <span className="Search-results-count">
                        {articles.length || 0} results found
                      </span>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                {loading ? (
                  <div className="mb-4">
                    <Placeholder as="h1" animation="glow">
                      <Placeholder xs={12} />
                    </Placeholder>
                  </div>
                ) : (
                  <>
                    <SearchBar fetchUrl={`?categories=${categoryId}&per_page=6&`} />
                  </>
                )}
              </>
            )}


            <div className="blogs-list">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <BlogsCard key={i} loading={true} />
                ))
              ) : articles.length === 0 ? (
                <p>No articles found for “{categoryName}”.</p>
              ) : (
                articles.map((article, index) => (
                  <button
                    key={index}
                    className="blog-link-wrapper"
                    type="button"
                    onClick={() => handleArticleClick(article.slug)}
                  >
                    <div className="blog-clickable">
                      <BlogsCard
                        article={article}
                        imageLoading={index === 0 ? "eager" : "lazy"}
                      />
                    </div>
                  </button>
                ))
              )}
            </div>

            {!loading && !fromSearch && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </div>

          <div className="tools-advertisement" id="Blogs-advertisement">
            <BlogsSideAds />
            <br />
            <Newsletter />
          </div>
        </div>

        <div className="ads">
          <BlogsBottomAds />
        </div>
      </main>
      <Footer />
    </>
  );
}
